'use client'

import { useActiveJourney } from '@/features/journeys/hooks/useActiveJourney'
import { useJourneysStore } from '@/features/journeys'

import { getCampaignContentAction } from '../domain/getCampaignContentAction'
import type { CampaignContent } from '../types/campaign'

import { useAppRouter } from '@/shared/lib/router'
import { CardTile, CardTileImage } from '@/shared/ui/Card'

interface CampaignContentTileProps {
  content: CampaignContent
  interactive?: boolean
  /** Campanha encerrada: o conteúdo não leva a lugar nenhum. */
  isCampaignEnded?: boolean
}

export function CampaignContentTile({
  content,
  interactive = true,
  isCampaignEnded = false
}: CampaignContentTileProps) {
  const router = useAppRouter()
  const { setActiveJourneyId } = useActiveJourney()
  const { journeys, getCurrentLevel } = useJourneysStore()
  const isJourney = content.content_type === 'Journey'

  // A campanha era a entrada que escapava do bloqueio de jornada: ela navegava
  // direto para o nível atual sem olhar o `unlock_journey_id`, enquanto o card
  // da lista de Jornadas já barrava. O estado vem da mesma fonte que a lista
  // usa, para os dois caminhos não divergirem de novo.
  const journey = isJourney
    ? journeys.find(item => item.id === content.content_id)
    : undefined
  const isBlocked = journey?._progress.blocked ?? false
  const blockedByJourneyName = journey?._progress.blockedByJourneyName

  const isInteractive = interactive && !isBlocked && !isCampaignEnded

  const handleClick = () => {
    if (!interactive) return

    const action = getCampaignContentAction({
      contentType: content.content_type,
      isBlocked,
      currentLevelUid: isJourney
        ? getCurrentLevel(content.content_id)?.level.uid
        : undefined,
      isCampaignEnded
    })

    if (action.kind === 'blocked') return

    if (isJourney) setActiveJourneyId(content.content_id)

    router.push(action.to)
  }

  return (
    <CardTile
      onClick={isInteractive ? handleClick : undefined}
      className={isInteractive ? 'cursor-pointer' : undefined}
    >
      <CardTileImage
        src={
          isJourney
            ? '/icons/journeys/Journey_Default_Icon.svg'
            : '/icons/missions.svg'
        }
        alt=""
        width={56}
        height={56}
      />
      <div className="flex min-w-0 grow flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#974319]/90">
          {isJourney ? 'Jornada' : 'Missão'}
        </span>
        <span className="truncate font-sans text-base font-semibold text-[#00298F]">
          {content.content_name ?? `Conteúdo #${content.content_id}`}
        </span>
        {isBlocked ? (
          <span className="text-xs font-medium text-[#7F7FAD]">
            {blockedByJourneyName
              ? `Conclua a jornada ${blockedByJourneyName} para desbloquear.`
              : 'Conclua a jornada anterior para desbloquear.'}
          </span>
        ) : null}
      </div>
      {isBlocked ? (
        <span className="shrink-0 rounded-full bg-white/70 px-2 py-1 text-xs font-bold text-[#7F7FAD]">
          Bloqueada
        </span>
      ) : interactive ? (
        <span className="shrink-0 rounded-full bg-white/70 px-2 py-1 text-xs font-bold text-jacaranda">
          Jogar
        </span>
      ) : null}
    </CardTile>
  )
}
